import { useState, useEffect, useRef } from 'react';

/**
 * Custom neon-green cursor for desktop visitors.
 * Renders two layered dots that follow the mouse, providing a subtle
 * glow effect in line with Unight's industrial-immersive aesthetic.
 * Automatically hides on touch devices.
 */
export const NeonCursor = () => {
  const cursorRef = useRef(null);
  const cursorGlowRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  useEffect(() => {
    if (isTouchDevice) return;

    const handleMouseMove = (e) => {
      if (cursorRef.current && cursorGlowRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
        cursorGlowRef.current.style.left = `${e.clientX}px`;
        cursorGlowRef.current.style.top = `${e.clientY}px`;
      }
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseLeave = () => setIsVisible(false);
    const handleMouseEnter = () => setIsVisible(true);

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [isVisible, isTouchDevice]);

  if (isTouchDevice) return null;

  return (
    <>
      <div
        ref={cursorRef}
        className={`fixed pointer-events-none z-[9998] transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        style={{
          width: '12px',
          height: '12px',
          backgroundColor: '#B5FF00',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 0 10px #B5FF00, 0 0 20px #B5FF00',
        }}
      />
      <div
        ref={cursorGlowRef}
        className={`fixed pointer-events-none z-[9997] transition-all duration-500 ${isVisible ? 'opacity-60' : 'opacity-0'}`}
        style={{
          width: '80px',
          height: '80px',
          background: 'radial-gradient(circle, rgba(181, 255, 0, 0.3) 0%, rgba(181, 255, 0, 0) 70%)',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          filter: 'blur(5px)',
        }}
      />
    </>
  );
};

/**
 * Full-bleed photo hero backdrop with a soft brand wash + vignette.
 */
export const CheckeredHeroBackground = ({ scrollY = 0 }) => {
  const HERO_BG = 'https://customer-assets.emergentagent.com/job_638d8ef7-2758-4c6d-8645-615661bf3f2b/artifacts/r8e9v1mv_Desktop%20BG.jpg';

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Photo base — crowd + green lasers (parallax, slight zoom for depth) */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${HERO_BG}')`,
          transform: `translateY(${scrollY * 0.15}px) scale(1.05)`,
          filter: 'saturate(1.1) contrast(1.05)',
        }}
      />

      {/* Soft brand wash to darken edges */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(181, 255, 0, 0.05) 0%, rgba(5, 5, 5, 0.35) 60%, rgba(5, 5, 5, 0.7) 100%)',
        }}
      />

      {/* Vignette for text legibility */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 25%, rgba(0, 0, 0, 0.55) 100%)',
        }}
      />

      {/* Bottom fade into the page body */}
      <div
        className="absolute inset-x-0 bottom-0 h-40"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, #050505 100%)',
        }}
      />
    </div>
  );
};
